import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { ClipboardService } from 'ngx-clipboard';
import { environment } from '../../../../../../environments/environment';
import { FormAlertType } from '../../../../../core/constants/constants';
import { ExternalEventsHandlerService } from '../../../../../core/services/external-events-handler.service';
import { AdminService } from '../../../../../core/services/admin.service';
import { XLaunchService } from '../../../../../core/services/x-launch.service';
import { RBACAuthGuardService } from '../../../../../core/guards/rbac-auth-guard.service';
import { ActionType, CategoryId } from 'src/app/core/utils/rbac.util';

@Component({
  selector: 'app-office365-dialog',
  templateUrl: './office365-dialog.component.html',
  styleUrls: ['./office365-dialog.component.scss'],
})

export class Office365DialogComponent implements OnInit, OnDestroy {

	constructor( @Inject(MAT_DIALOG_DATA) public data: any,
				public dialogRef: MatDialogRef<Office365DialogComponent>,
				private translateService: TranslateService,
				private domSanitizer: DomSanitizer,
				private matIconRegistry: MatIconRegistry,
				private clipboardService: ClipboardService,
				private extEventHandlerService: ExternalEventsHandlerService,
				private xlaunchService: XLaunchService,
				private rbacAuthGuardService: RBACAuthGuardService,
				private adminService: AdminService ) 
	{
		this.matIconRegistry.addSvgIcon(
			'trash_can',
			this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/images/trash.svg")
		);
		
		this.matIconRegistry.addSvgIcon(
			'external',
			this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/images/external.svg")
		);
	}

	tenantUrl: string = undefined;
	secretToken: string = undefined;
	showFormAlert: boolean = false;
	alertMsg: string = "";
	alertMsgStyle: any = { 'margin': '32px 32px 0' };
	alertType: FormAlertType;
	theCopyLabel: string;
	primaryStr: string;
	secondaryStr: string;
	stringToCopy: string;
	verifiedDate: string = "";
	verifiedTime: string = "";
	isVerified:boolean = false;
	isEnabled:boolean = false;
	subscription: Subscription;
	canRemove: boolean = false;

	ngOnInit() 
	{
		let product = this.data.product;
		this.primaryStr =  this.translateService.instant('integrations.office365.copy-url');
		this.secondaryStr =  this.translateService.instant('integrations.office365.copied-url');
		this.theCopyLabel = this.primaryStr;
		let theCurrentState = {
			accountId: this.data.account.accountId,
			context: "Office365Consent"
		};
		console.log("The state before encoding", theCurrentState);
		let theState = encodeURIComponent(JSON.stringify(theCurrentState));
		console.log("The state after encoding", theState);
		console.log("location.origin", location.origin)

		this.stringToCopy = "https://login.microsoftonline.com/common/adminconsent?client_id=" 
			+ environment.office365ClientId 
			+ "&redirect_uri=" + location.origin + "&state=" + theState; 

		this.subscription = this.extEventHandlerService.office365ConsentReceived.subscribe( event => {
			console.log("Received CONSENT event", event)
			this.handleConsentMessage(event);
		});
		this.updateDialogStateBasedOnTags();
		this.determineIfUserCanRemove();

	}
	
	// can not be solved using the directive
	// The user can always delete this integration if they are x-launched otherwise it depends on the user ROLE
	determineIfUserCanRemove()
	{
		this.canRemove = this.xlaunchService.isXLaunchedFromBoss() || 
			(!this.rbacAuthGuardService.isPermissionDeniedForUser(CategoryId.INTEGRATIONS, ActionType.DELETE));
	}

	onRemoveIntegration()
	{
		console.log("Remove the integration - when successful, close the dialog");
		let result = 
		{
			remove: true,
			//is there anything else?
		}
		this.dialogRef.close( result);
	}

	onEnableAndDone(event)
	{	
		console.log("DONE AND ENABLED");
		let result = 
		{
			setupComplete: true,
			enabledManually: true
			//is there anything else?
		}
		this.dialogRef.close( result);
	}

	async onDone(event)
	{

		// so apparently we need to consider the CL admin keeping the dialog open while the 
		// Office 365 admin consents, so lets check to see if the tags were updated
		await this.updateDialogStateBasedOnTags();
		let complete = this.isVerified || this.isEnabled;
		let result = 
		{
			setupComplete: complete,
			//is there anything else?
		}
		this.dialogRef.close( result);
	}

	onAuthorizeOffice365()
	{
		let theCurrentState = {
			accountId: this.data.account.accountId,
		};

		this.showFormAlert = false;
		this.alertMsg = "";
		let theState = encodeURIComponent(JSON.stringify(theCurrentState));
		console.log("State to pass", theState);
		console.log("location.origin", location.origin)
		let theUrl = "https://login.microsoftonline.com/common/adminconsent?client_id=" 
			+ environment.office365ClientId 
			+ "&redirect_uri=" + location.origin + "&state=" + theState; 
		console.log("This is the url", theUrl);
		let params = `scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,width=600,height=700,left=300,top=100`;
		window.open(theUrl, '', params);
	}

	onCopyUrl()
	{
		this.theCopyLabel = this.secondaryStr;
	}

	updateDialogStateBasedOnTags()
	{
		return this.adminService.getAccountTags( this.data.account.accountId ).then( tags => {
			if ( tags && tags.Office365 && tags.Office365.verifiedDate )
			{
				// display the verified date
				var d = new Date(tags.Office365.verifiedDate);

				this.verifiedDate = d.toLocaleDateString();
				this.verifiedTime = d.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit', timeZoneName: 'short'});
			}

			if ( tags && tags.Office365 && tags.Office365.consent != undefined )
			{
				this.isVerified = tags.Office365.consent;
				this.isEnabled = tags.Office365.enabled;
			}			
		});
	}
	
	handleConsentMessage( event: any )
	{
		console.log("Handle the event", event)
		if ( event.granted == true )
		{
			console.log("CONSENT GRANTED, show message")
			this.alertMsg = this.translateService.instant('integrations.office365.consent-success');
			this.alertType = FormAlertType.SUCCESS;
			this.showFormAlert = true;
			this.updateDialogStateBasedOnTags();
		}
		else if ( event.granted == false )
		{
			console.log("CONSENT FAILED, show message")
			if ( event.error == "access_denied" && (event.description == undefined || event.description == '') )
			{
				this.alertMsg = this.translateService.instant('integrations.office365.consent-failure-access-denied');
			}
			else if ( event.error == "access_denied" && (event.description != undefined || event.description != '') )
			{
				if ( event.description.indexOf('AADSTS65004') != -1 )
				{
					// this means an Office 365 admin declined to give consent
					this.alertMsg = this.translateService.instant('integrations.office365.consent-failure-declined');
				}
				else
				{
					// we do not know other messages so we will show generic
					this.alertMsg = this.translateService.instant('integrations.office365.consent-failure-general') + " " + 
						event.description;
				}
			}
			else
			{
				this.alertMsg = this.translateService.instant('integrations.office365.consent-failure-general');

				if ( event.description != undefined && event.description != '' )
					this.alertMsg = this.alertMsg + " " + event.description;
			}
			console.log("Show message", this.alertMsg);
			this.alertType = FormAlertType.ERROR;
			this.showFormAlert = true;
		}
	}

	ngOnDestroy()
	{
		this.subscription.unsubscribe();
	}
}
